/* =========================================================================
 * IBC 2.0 — DIALOG
 * -------------------------------------------------------------------------
 * One accessible modal surface, used by the mobile navigation, the booking
 * chooser, the cart drawer and quick add. Focus is moved in, trapped while
 * open and returned on close. Escape and a click on the scrim both dismiss.
 *
 * Markup contract:
 *   <ibc-dialog id="x" class="dialog dialog--drawer" hidden>
 *     <button class="dialog__scrim" data-dialog-close type="button"
 *             aria-label="Close"></button>
 *     <div class="dialog__panel" role="dialog" aria-modal="true"
 *          aria-labelledby="x-title" tabindex="-1"> … </div>
 *   </ibc-dialog>
 *
 * Any element with `data-dialog-open="<id>"` toggles it; any element inside
 * with `data-dialog-close` closes it.
 * ====================================================================== */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  /* Move focus without letting the browser scroll the focused control into
   * view. On opening, the panel must be shown from its top — scrolling it to
   * the first form control hides the image, the title and the price above the
   * fold. A browser without the options bag ignores the argument and still
   * focuses; the catch is for the older engines that throw on it. */
  function focusNoScroll(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (ignore) {
      el.focus();
    }
  }

  var IbcDialog = function () {};

  if (typeof HTMLElement === 'function') {
    IbcDialog = class extends HTMLElement {
      constructor() {
        super();
        this.open = false;
        this._returnFocus = null;
        this._onKey = this._onKey.bind(this);
        this._onClick = this._onClick.bind(this);
      }

      connectedCallback() {
        this.panel = this.querySelector('.dialog__panel');
        if (this.panel) {
          this.panel.setAttribute('role', this.panel.getAttribute('role') || 'dialog');
          this.panel.setAttribute('aria-modal', 'true');
          if (!this.panel.hasAttribute('tabindex')) this.panel.setAttribute('tabindex', '-1');
        }
        this.addEventListener('click', this._onClick);
        if (!this.open) this.hidden = true;
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        document.removeEventListener('keydown', this._onKey, true);
        if (this.open) document.body.classList.remove('no-scroll');
        this.open = false;
      }

      _onClick(event) {
        var closer = event.target.closest('[data-dialog-close]');
        if (closer && this.contains(closer)) {
          event.preventDefault();
          this.hide();
        }
      }

      _onKey(event) {
        if (!this.open) return;
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.stopPropagation();
          event.preventDefault();
          this.hide();
          return;
        }
        if (event.key !== 'Tab' || !this.panel) return;
        var items = Array.prototype.filter.call(
          this.panel.querySelectorAll(FOCUSABLE),
          visible
        );
        if (!items.length) {
          event.preventDefault();
          this.panel.focus();
          return;
        }
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      show(opener) {
        if (this.open) return;
        this.open = true;
        this._returnFocus =
          opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        this.hidden = false;
        document.body.classList.add('no-scroll');
        /* Force a frame so the transition runs from its start state. */
        void this.offsetWidth;
        this.classList.add('is-open');
        document.addEventListener('keydown', this._onKey, true);

        var panel = this.panel;
        window.setTimeout(function () {
          if (!panel) return;
          var target = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), visible)[0];
          focusNoScroll(target || panel);
        }, 30);

        this.dispatchEvent(new CustomEvent('ibc:dialog:open', { bubbles: true }));
      }

      hide() {
        if (!this.open) return;
        this.open = false;
        this.classList.remove('is-open');
        document.removeEventListener('keydown', this._onKey, true);
        document.body.classList.remove('no-scroll');

        var self = this;
        var finish = function () {
          if (!self.open) self.hidden = true;
        };
        var reduced =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.setTimeout(finish, reduced ? 0 : 380);

        if (this._returnFocus && document.contains(this._returnFocus)) {
          this._returnFocus.focus();
        }
        this._returnFocus = null;

        this.dispatchEvent(new CustomEvent('ibc:dialog:close', { bubbles: true }));
      }

      toggle(opener) {
        if (this.open) {
          this.hide();
        } else {
          this.show(opener);
        }
      }
    };

    if (!window.customElements.get('ibc-dialog')) {
      window.customElements.define('ibc-dialog', IbcDialog);
    }
  }

  /* Global delegation: one listener, survives section reloads. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-dialog-open]');
    if (!trigger) return;
    var id = trigger.getAttribute('data-dialog-open');
    var dialog = document.getElementById(id);
    if (!dialog || typeof dialog.toggle !== 'function') return;
    event.preventDefault();
    dialog.toggle(trigger);
    if (trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', dialog.open ? 'true' : 'false');
    }
  });

  /* Keep any aria-expanded trigger honest when the dialog closes itself. */
  document.addEventListener('ibc:dialog:close', function (event) {
    var id = event.target && event.target.id;
    if (!id) return;
    var triggers = document.querySelectorAll('[data-dialog-open="' + id + '"][aria-expanded]');
    for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-expanded', 'false');
  });

  document.addEventListener('ibc:dialog:open', function (event) {
    var id = event.target && event.target.id;
    if (!id) return;
    var triggers = document.querySelectorAll('[data-dialog-open="' + id + '"][aria-expanded]');
    for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-expanded', 'true');
  });

  window.IbcDialog = IbcDialog;
})();
